<script setup lang="ts">
import { computed } from 'vue'
import { QItem, QItemSection } from 'quasar'
import { Color } from '@previo/codebooks'
import { $t } from '../../../composables/i18n'
import PCheckboxGroup from '../../fields/PCheckboxGroup.vue'
import PContextMenu from '../../context-menu/PContextMenu.vue'
import PDivider from '../../divider/PDivider.vue'
import PDragAndDropList from '../../drag-and-drop-list/PDragAndDropList.vue'
import PSpace from '../../space/PSpace.vue'
import { PSpaceSize } from '../../space/types'
import { type PDataTableColumn } from '../types'
import PTextFormBody from '../../typography/form/PTextFormBody.vue'

type Props = {
  columns: PDataTableColumn[]
}

type Emits = {
  (e: 'reset'): void
}

const isOpen = defineModel<boolean>('isOpen', { default: false })
const visibleColumns = defineModel<PDataTableColumn['name'][]>('visibleColumns')
const columnOrder = defineModel<PDataTableColumn['name'][]>('columnOrder', { default: [] })

const props = defineProps<Props>()

const emits = defineEmits<Emits>()

const onResetClick = (): void => {
  emits('reset')
}

const orderedColumnNames = computed<PDataTableColumn['name'][]>(() => {
  const names = props.columns.map((c) => c.name)
  const kept = columnOrder.value.filter((name) => names.includes(name))
  const added = names.filter((name) => !kept.includes(name))
  return kept.length > 0 ? [...kept, ...added] : names
})

const orderByColumnOrder = (names: PDataTableColumn['name'][]) =>
  orderedColumnNames.value.filter((name) => names.includes(name))

const draggableAllColumns = computed({
  get: () => orderedColumnNames.value,
  set: (newOrder: PDataTableColumn['name'][]) => {
    columnOrder.value = newOrder
    visibleColumns.value = orderByColumnOrder(visibleColumns.value ?? [])
  },
})

const getColumnLabel = (name: string) => props.columns.find((c) => c.name === name)?.label || name
</script>

<template>
  <p-context-menu
    v-model="isOpen"
    :title="$t('Select visible columns')"
    cy="data-table-toggle-columns-visibility-menu"
  >
    <p-drag-and-drop-list v-model="draggableAllColumns">
      <template v-for="col in draggableAllColumns" #[`item.${col}`] :key="col">
        <q-item class="p-column-order-item p-flex p-items-center" tag="label">
          <q-item-section>
            <div class="p-flex p-items-center p-no-wrap">
              <p-checkbox-group
                v-model="visibleColumns"
                :label="getColumnLabel(col)"
                :value="col"
                cy="data-table-toggle-columns-visibility-checkbox"
              />
            </div>
          </q-item-section>
        </q-item>
      </template>
    </p-drag-and-drop-list>

    <p-space :mobile="PSpaceSize.XS" vertical />
    <p-divider />
    <p-space :mobile="PSpaceSize.XS" vertical />

    <q-item tag="label" @click="onResetClick">
      <q-item-section>
        <p-text-form-body :text="$t('Reset columns')" :color="Color.PRIMARY" />
      </q-item-section>
    </q-item>
  </p-context-menu>
</template>

<style lang="scss" scoped>
:deep(.ds-drag-item) {
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>
